"use client";

// imports
import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';

// lib files
import {
  locateAtom,
  PageStatusEnum,
} from '@/lib/atoms/location-verification';
import { useHasMounted } from '@/lib/hooks';

// components
import Button from '@/components/Button/Button';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';

// svgs
import CloseIcon from '@/public/svg/close.svg';

// styles
import styles from './ShareLocationPrompt.module.scss';


const ShareLocationPrompt = () => {
  const [locateState, setLocateState] = useAtom(locateAtom);
  const [ locationError, setLocationError ] = useState<string | null>(null);
  const [ isRequestingLocation, setIsRequestingLocation ] = useState<boolean>(false);
  const hasMounted = useHasMounted();

  const geoSuccess = useCallback((position: GeolocationPosition) => {
    const { coords } = position;
    setLocateState({
      ...locateState,
      lat: coords.latitude,
      long: coords.longitude,
      pageStatus: PageStatusEnum.locateCodeAndLocationLoading,
    });

    setIsRequestingLocation(false);
  }, [locateState, setLocateState, setIsRequestingLocation]);

  const geoError = useCallback((_error: GeolocationPositionError) => {
    setLocationError(
      'Please enable location sharing on your device or browser and refresh the page, or try entering your code on another mobile device, tablet, or computer.'
    )
    setIsRequestingLocation(false);
  }, [setLocationError, setIsRequestingLocation]);

  const handleShareLocationButtonClick = () => {
    if (!navigator.geolocation) {
      return setLocationError("Geolocation is not supported by your browser.");
    }

    setIsRequestingLocation(true);

    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    };

    navigator.geolocation.getCurrentPosition(geoSuccess, geoError, options);
  }

  if (!hasMounted) {
    return null;
  }

  return (
    <div className={styles.share_location_prompt}>
      <h1 className={styles.share_location_prompt__headline}>Verify your location</h1>
      <p className={styles.share_location_prompt__subhead}><em>Allow PBS to verify your device location.</em></p>
      <p className={styles.share_location_prompt__explanation}>
      Your device must be capable of sharing its location, which PBS will only use temporarily to access your station&rsquo;s livestream. We will not store your location after you&rsquo;ve shared it.
      </p>
      <Button
        onClick={handleShareLocationButtonClick}
        disabled={isRequestingLocation}
        style="blue"
        className={styles.share_location_prompt__button}
      >
        Share My Location
      </Button>
      { isRequestingLocation && (
        <div className={styles.share_location_prompt__loading}>
          <LoadingSpinner />
          <p>Requesting device location…</p>
        </div>
      )}
      { locationError && (
        <div className={styles.share_location_prompt__error_message}>
          <p><CloseIcon /> Sorry, we couldn&rsquo;t access your location</p>
          <p>{locationError}</p>
        </div>
      )}
    </div>
  );
};

export default ShareLocationPrompt;
